import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useAuthStore } from '../../../stores/useAuthStore';
import { useChatStore } from '../../../stores/useChatStore';
import { useInteractionStore } from '../../../stores/useInteractionStore';
import { useDataStore } from '../../../stores/useDataStore';
import useServiceOrderStore from '../../../stores/useServiceOrderStore';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const token = useAuthStore((state) => state.token);
  const [socket, setSocket] = useState(null);

  const initRef = useRef(false);
  const onlineUsers = useChatStore((state) => state.onlineUsers);

  useEffect(() => {
    if (initRef.current) return;
    if (!isLoggedIn || !currentUser) return;

    const authToken = token || localStorage.getItem('token');
    if (!authToken) return;

    initRef.current = true;

    const newSocket = io(import.meta.env.VITE_SOCKET_SERVER_URL, {
      auth: { token: authToken, roles: currentUser.roles || [] },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      connectTimeout: 15000,
      pingInterval: 25000,
      pingTimeout: 60000,
    });

    newSocket.on('connect', () => {
      console.info('[socket] connected', { id: newSocket.id, userId: currentUser._id });
      newSocket.emit('join', { userId: currentUser._id, roles: currentUser.roles || [] });
    });

    newSocket.on('initial-online-users', (users) => {
      const normalized = Array.isArray(users)
        ? users.map((id) => id?.toString()).filter(Boolean)
        : [];
      useChatStore.getState().setOnlineUsers(normalized);
    });

    newSocket.on('user-online', (userId) => {
      if (!userId) return;
      useChatStore.getState().handleUserOnline(userId.toString());
    });

    newSocket.on('user-offline', (userId) => {
      if (!userId) return;
      useChatStore.getState().handleUserOffline(userId.toString());
    });

    newSocket.on('typing', (payload) => {
      if (!payload?.conversationId || !payload?.user?._id) return;
      useChatStore.getState().setTypingStatus(
        payload.conversationId,
        payload.user._id,
        Boolean(payload.isTyping)
      );
    });

    newSocket.on('new-message', (message) => {
      if (!message?.conversationId || !message?._id) return;
      useChatStore.getState().addMessage(message.conversationId, message);
    });

    newSocket.on('message-delivered', ({ conversationId, messageId, deliveredAt }) => {
      if (!conversationId || !messageId) return;
      useChatStore.getState().updateMessage(conversationId, messageId, { deliveredAt, status: 'delivered' });
    });

    newSocket.on('messages-read', ({ conversationId, readerId }) => {
      if (!conversationId || !readerId) return;
      useChatStore.getState().markConversationRead(conversationId, readerId);
    });

    newSocket.on('new-notification', (notification) => {
      useInteractionStore.getState().addNotification?.(notification);
    });

    newSocket.on('story-update', () => {
      useDataStore.getState().fetchStories?.();
    });

    newSocket.on('new_service_order', (payload) => {
      if (!payload?.orderId) return;
      useServiceOrderStore.getState().updateOrderFromSocket?.(payload.orderId, payload);
    });

    newSocket.on('disconnect', (reason) => {
      console.warn('[socket] disconnected', { reason });
      useChatStore.getState().setOnlineUsers([]);
    });

    newSocket.on('connect_error', (error) => {
      console.warn('[socket] connect_error', { message: error?.message || error });
    });

    setSocket(newSocket);

    return () => {
      newSocket.removeAllListeners();
      newSocket.close();
      initRef.current = false;
      setSocket(null);
    };
  }, [isLoggedIn, currentUser, token]);

  const value = useMemo(() => ({ socket, onlineUsers }), [socket, onlineUsers]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
